/**
 * PHARMAKON — Alertes équipe : le magasin d'affichage (web).
 *
 * PLAN_mobile_pharma_owner_manager §3.4 (diffusion et budget de latence) et
 * §3.5 (contrat d'affichage web).
 *
 * CE QUE CE MAGASIN TIENT
 *   • le SONDAGE — le chemin de CORRECTION, toujours armé, cadence dictée par
 *     le serveur (L-2 : aucun délai n'est inventé ici) ;
 *   • le FLUX SSE — une simple optimisation de latence posée par-dessus ;
 *   • la répartition des alertes en trois files d'affichage (bandeau / toast
 *     persistant / surcouche bloquante) ;
 *   • les gestes « vu » et « accusé », optimistes puis réconciliés au sondage.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * L'INVARIANT QUI COMPTE : le SONDAGE seul suffit.
 *
 * Coupez le SSE, redémarrez le serveur, déployez en multi-réplique, laissez le
 * poste éteint toute la nuit : l'ensemble « en attente » est DÉRIVÉ DE LA BASE à
 * chaque sondage, jamais accumulé depuis un flux d'événements. Un URGENT émis
 * pendant que le poste était éteint s'affiche au démarrage suivant, par
 * construction. Le SSE ne fait que raccourcir la médiane ; il ne doit JAMAIS
 * devenir la source de vérité (voir la note mono-réplique dans
 * `config/alerte-equipe.config.js`).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { create } from "zustand";
import * as api from "../services/alerteEquipe.api.js";
import { ouvrirFlux } from "../services/alerteEquipe.sse.js";
import { diffuserResync, diffuserSujet } from "../services/sujetsTempsReel.js";
import {
  ALERTE_CLIENT_DEFAUTS,
  ACK_SOURCE,
} from "../components/alertes/alerte-equipe.config.js";

// ═════════════════════════════════════════════════════════════════════════════
// AIDES PURES — testables en environnement node, sans DOM ni réseau
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Normalise le bloc de cadence rendu par le serveur.
 *
 * Le serveur fait autorité (L-2). Les défauts client ne servent qu'AVANT la
 * toute première réponse — jamais comme seconde source de vérité.
 *
 * @param {object|null} reponse
 * @param {object} [courante] la cadence déjà connue, conservée champ par champ
 */
export function fusionnerCadence(reponse, courante = ALERTE_CLIENT_DEFAUTS) {
  const r = reponse ?? {};
  const n = (v, repli) => (Number.isFinite(Number(v)) ? Number(v) : repli);
  return {
    pollMs: n(r.poll_ms, courante.pollMs),
    pollVisibleMs: n(r.poll_visible_ms, courante.pollVisibleMs ?? courante.pollMs),
    pollCacheMs: n(r.poll_cache_ms, courante.pollCacheMs),
    pollUrgentMs: n(r.poll_urgent_ms, courante.pollUrgentMs),
    pollStreamMs: n(r.poll_stream_ms, courante.pollStreamMs),
    backoffMaxMs: n(r.backoff_max_ms, courante.backoffMaxMs),
    echecsAvantBackoff: n(r.echecs_avant_backoff, courante.echecsAvantBackoff),
    maxDeferMs: n(r.max_defer_ms, courante.maxDeferMs),
    maxBannieres: n(r.max_bannieres, courante.maxBannieres),
    sseEnabled:
      typeof r.sse_enabled === "boolean" ? r.sse_enabled : courante.sseEnabled,
  };
}

/**
 * Le délai avant le PROCHAIN sondage.
 *
 * L'ordre des règles est le contrat §3.4, et il n'est pas commutatif :
 *   1. en repli d'échec réseau, le repli exponentiel plafonné gagne sur tout —
 *      sinon un serveur tombé serait martelé toutes les 5 s ;
 *   2. sinon un URGENT non accusé impose la cadence rapide, MÊME onglet caché :
 *      le comptoir peut être devant un autre onglet et l'alerte doit arriver ;
 *   3. sinon onglet caché → cadence lente ;
 *   4. sinon, flux SSE vivant → cadence « plancher de correction » (rare, long),
 *      puisque la latence est déjà tenue par le flux ;
 *   5. sinon la cadence visible ordinaire.
 *
 * @returns {number} millisecondes
 */
export function cadenceEffective({
  cadence = ALERTE_CLIENT_DEFAUTS,
  visible = true,
  urgentEnAttente = false,
  streamVivant = false,
  echecs = 0,
}) {
  const seuil = cadence.echecsAvantBackoff ?? ALERTE_CLIENT_DEFAUTS.echecsAvantBackoff;
  const plafond = cadence.backoffMaxMs ?? ALERTE_CLIENT_DEFAUTS.backoffMaxMs;
  const base = cadence.pollVisibleMs ?? cadence.pollMs ?? ALERTE_CLIENT_DEFAUTS.pollMs;

  if (echecs >= seuil) {
    // 2^(n - seuil) × base, plafonné. Trois échecs → base, quatre → 2×base…
    const facteur = 2 ** Math.min(echecs - seuil, 20);
    return Math.min(base * facteur, plafond);
  }
  if (urgentEnAttente) return cadence.pollUrgentMs ?? ALERTE_CLIENT_DEFAUTS.pollUrgentMs;
  if (!visible) return cadence.pollCacheMs ?? ALERTE_CLIENT_DEFAUTS.pollCacheMs;
  if (streamVivant) return cadence.pollStreamMs ?? ALERTE_CLIENT_DEFAUTS.pollStreamMs;
  return base;
}

/**
 * Répartit le flux d'affichage en trois files, selon le contrat §3.5.
 *
 * On lit `display` / `niveau_affiche` — JAMAIS `niveau` : c'est le serveur qui
 * décide du rendu, et l'interrupteur général `ALERTES_EQUIPE_URGENT_ENABLED`
 * dégrade un CRITICAL en toast sans redéploiement. Un client qui lirait
 * `niveau` court-circuiterait cet interrupteur — c'est-à-dire la seule façon
 * d'arrêter une surcouche bloquante partie de travers sans livrer du code.
 *
 * L'ordre est celui rendu par le serveur (palier décroissant, plus ancien
 * d'abord) : il n'est PAS retrié ici, sinon « la plus ancienne d'abord » de la
 * file bloquante dépendrait du fuseau du poste.
 */
export function repartir(alertes = [], maxBannieres = ALERTE_CLIENT_DEFAUTS.maxBannieres) {
  const bloquantes = [];
  const toasts = [];
  const bannieresToutes = [];

  for (const a of alertes) {
    const display = a?.display ?? contratParDefaut(a?.niveau_affiche);
    if (display === "BLOQUANT") bloquantes.push(a);
    else if (display === "TOAST") toasts.push(a);
    else bannieresToutes.push(a);
  }

  const limite = Math.max(1, Number(maxBannieres) || 1);
  return {
    bloquantes,
    toasts,
    bannieres: bannieresToutes.slice(0, limite),
    // Le repli « +N » : les bandeaux au-delà de la limite ne sont pas perdus,
    // ils sont comptés. Zéro alerte n'est jamais silencieusement jetée.
    bannieresEnTrop: Math.max(0, bannieresToutes.length - limite),
  };
}

/** Repli de rendu quand le serveur n'a pas envoyé `display` (client plus vieux). */
function contratParDefaut(niveauAffiche) {
  if (niveauAffiche === "CRITICAL") return "BLOQUANT";
  if (niveauAffiche === "WARNING") return "TOAST";
  return "BANNIERE";
}

/** Y a-t-il un URGENT non accusé ? — pilote la cadence rapide. */
export function urgentEnAttente(alertes = []) {
  return alertes.some(
    (a) => (a?.display ?? contratParDefaut(a?.niveau_affiche)) === "BLOQUANT",
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LE MAGASIN
// ═════════════════════════════════════════════════════════════════════════════

/** Un `setTimeout` nommé, pour ne jamais en laisser fuir un. */
function poser(fn, ms) {
  const t = setTimeout(fn, ms);
  return () => clearTimeout(t);
}

export const useAlerteStore = create((set, get) => ({
  // ── État ──────────────────────────────────────────────────────────────────
  alertes: [],
  compteurs: { info: 0, important: 0, urgent: 0, total: 0, max_niveau: null },
  version: null,
  cadence: { ...ALERTE_CLIENT_DEFAUTS },
  /** Échecs réseau CONSÉCUTIFS — remis à zéro par le premier succès. */
  echecs: 0,
  /** Le badge dégradé du §3.4 : « je ne sais plus si une alerte est arrivée ». */
  degrade: false,
  /** Le flux SSE est-il vivant ? (informatif — la correction reste au sondage) */
  streamVivant: false,
  actif: false,
  /** Alertes rejetées localement, le temps que le serveur le confirme. */
  enCoursDeRejet: [],

  // ── Cycle de vie ──────────────────────────────────────────────────────────

  /**
   * Démarre le sondage (et le flux si le serveur l'autorise). Idempotent : un
   * double montage de l'hôte ne pose pas deux boucles.
   */
  demarrer: () => {
    if (get().actif) return;
    set({ actif: true });
    get()._planifier(0);
    get()._ouvrirStream();
  },

  /** Arrête tout et relâche les ressources. */
  arreter: () => {
    const { _annulerTimer, _abandonStream } = get();
    _annulerTimer?.();
    _abandonStream?.();
    set({
      actif: false,
      streamVivant: false,
      _annulerTimer: null,
      _abandonStream: null,
    });
  },

  /** Interne — le prochain sondage. */
  _annulerTimer: null,
  _abandonStream: null,
  _lastEventId: null,
  /** Le flux a-t-il déjà été ouvert une fois (une reconnexion « NEUF » a pu manquer des trames) ? */
  _dejaConnecte: false,

  _planifier: (delaiMs) => {
    const { _annulerTimer } = get();
    _annulerTimer?.();
    const annuler = poser(() => {
      if (!get().actif) return;
      get().sonder();
    }, Math.max(0, delaiMs));
    set({ _annulerTimer: annuler });
  },

  /**
   * UN tour de sondage : le compteur chaud, puis `/actives` SEULEMENT si la
   * version a bougé. C'est la forme que la cloche de notifications utilise déjà
   * — un compteur pas cher, une lecture complète uniquement quand il change.
   */
  sonder: async () => {
    if (!get().actif) return;
    try {
      const c = await api.compteur();
      const cadence = fusionnerCadence(c, get().cadence);
      const versionServeur = c?.version ?? null;
      const aChange = versionServeur !== get().version;

      set({
        compteurs: {
          info: c?.info ?? 0,
          important: c?.important ?? 0,
          urgent: c?.urgent ?? 0,
          total: c?.total ?? 0,
          max_niveau: c?.max_niveau ?? null,
        },
        cadence,
        echecs: 0,
        degrade: false,
      });

      if (aChange) await get().recharger(versionServeur);
    } catch (e) {
      // Un 401 est traité par l'intercepteur partagé (rafraîchissement, puis
      // déconnexion). Ici on ne fait QUE compter l'échec : on ne vide jamais
      // `alertes`, sinon une coupure réseau ferait disparaître une surcouche
      // bloquante non accusée — exactement le contraire du contrat.
      const echecs = get().echecs + 1;
      const seuil =
        get().cadence.echecsAvantBackoff ?? ALERTE_CLIENT_DEFAUTS.echecsAvantBackoff;
      set({ echecs, degrade: echecs >= seuil });
      if (import.meta.env?.DEV) {
         
        console.debug("[AlerteEquipe] sondage en échec", echecs, e?.message ?? e);
      }
    } finally {
      if (get().actif) get()._planifier(get()._delaiCourant());
    }
  },

  /** Recharge le flux complet. `version` vient du sondage qui l'a déclenché. */
  recharger: async (versionAttendue = undefined) => {
    const r = await api.actives();
    const liste = Array.isArray(r?.data) ? r.data : [];
    set({
      alertes: liste,
      version: versionAttendue !== undefined ? versionAttendue : (r?.version ?? null),
      cadence: fusionnerCadence(r, get().cadence),
      // Le serveur vient de parler : ce qu'on avait rejeté localement et qu'il
      // ne rend plus est confirmé ; ce qu'il rend encore revient à l'écran.
      enCoursDeRejet: get().enCoursDeRejet.filter((id) =>
        liste.some((a) => a.id === id),
      ),
    });
    return liste;
  },

  /** Le délai avant le prochain tour, selon l'état courant. */
  _delaiCourant: () => {
    const s = get();
    const visible =
      typeof document === "undefined" ? true : document.visibilityState !== "hidden";
    return cadenceEffective({
      cadence: s.cadence,
      visible,
      urgentEnAttente: urgentEnAttente(s.alertes) || s.compteurs.urgent > 0,
      streamVivant: s.streamVivant,
      echecs: s.echecs,
    });
  },

  /** Sonde MAINTENANT (retour d'onglet, événement SSE, geste utilisateur). */
  sonderMaintenant: () => {
    if (!get().actif) return;
    get()._planifier(0);
  },

  // ── Gestes ────────────────────────────────────────────────────────────────

  /**
   * « Vu » — le rejet explicite d'un bandeau ou d'un toast (§3.5 : le geste est
   * le REJET, pas le rendu). Optimiste : on retire tout de suite, le sondage
   * réconcilie. Si l'appel échoue, l'alerte revient — c'est voulu.
   */
  marquerVu: async (id) => {
    set((s) => ({
      alertes: s.alertes.filter((a) => a.id !== id),
      enCoursDeRejet: [...s.enCoursDeRejet, id],
    }));
    try {
      await api.marquerVu(id);
    } catch {
      /* le prochain sondage la fera réapparaître — jamais de perte silencieuse */
    } finally {
      get().sonderMaintenant();
    }
  },

  /**
   * Accusé de réception explicite (palier URGENT).
   *
   * NON optimiste, contrairement à « vu » : la surcouche ne se démonte QUE si
   * le serveur a répondu 200 (§3.5). En cas d'échec elle reste, avec une
   * relance — sinon on aurait un comptoir débloqué sans trace d'accusé, ce que
   * le tableau du titulaire lirait comme « non vu ».
   *
   * @returns {Promise<boolean>} true si le serveur a bien enregistré
   */
  accuser: async (id) => {
    try {
      await api.accuser(id, ACK_SOURCE);
      set((s) => ({ alertes: s.alertes.filter((a) => a.id !== id) }));
      get().sonderMaintenant();
      return true;
    } catch {
      return false;
    }
  },

  // ── Le flux SSE (latence seulement) ───────────────────────────────────────

  _ouvrirStream: () => {
    const s = get();
    if (!s.actif || !s.cadence.sseEnabled) return;
    if (s._abandonStream) return;
    if (typeof AbortController === "undefined" || typeof fetch === "undefined") return;

    const controleur = new AbortController();
    set({ _abandonStream: () => controleur.abort() });

    ouvrirFlux({
      signal: controleur.signal,
      lastEventId: get()._lastEventId,
      onEvenement: (e) => {
        if (e.id) set({ _lastEventId: e.id });
        if (e.commentaire) return; // battement : la connexion respire, rien à faire
        if (e.event === "hello") {
          set({ streamVivant: true, cadence: fusionnerCadence(e.data, get().cadence) });
          // `reprise: RESYNC` veut dire « mon tampon ne te couvre plus » : on
          // recharge, sans supposer quoi que ce soit.
          if (e.data?.reprise === "RESYNC") {
            get().sonderMaintenant();
            diffuserResync();
          } else if (e.data?.reprise === "NEUF" && get()._dejaConnecte) {
            // Trésorerie TWP-11 — une RECONNEXION sans identifiant à reprendre (aucune trame
            // numérotée reçue avant la coupure) n'a rien pu rejouer : les écouteurs de sujets
            // relisent ce qu'ils affichent, exactement comme après un `resync`.
            diffuserResync();
          }
          set({ _dejaConnecte: true });
          return;
        }
        // Cross-module X XA-09 — sujet d'invalidation (ex. « chifa:facture:<poste> ») : redistribué
        // aux écrans montés, jamais une alerte (aucun sondage des alertes déclenché).
        if (e.event === "sujet") {
          diffuserSujet(e.data);
          return;
        }
        if (e.event === "resync") diffuserResync();
        if (e.event === "resync" || e.event === "alerte") {
          // Le flux ne PORTE pas le contenu (il ne transporte que
          // `{type, alerte_id, niveau}`) : il déclenche une lecture authentifiée.
          get().sonderMaintenant();
        }
        // `bye` : le serveur a fermé pour durée max — la boucle ci-dessous
        // se chargera de rouvrir.
      },
    })
      .catch(() => ({ raison: "RUPTURE" }))
      .then((fin) => {
        set({ streamVivant: false, _abandonStream: null });
        if (!get().actif) return;
        if (fin?.raison === "ABANDON") return;
        // Flux coupé : on NE PANIQUE PAS. Le sondage tient la correction ; on
        // retente d'ouvrir au rythme du sondage plancher.
        const attente =
          fin?.raison === "INDISPONIBLE" || fin?.raison === "SANS_JETON"
            ? get().cadence.pollStreamMs
            : get().cadence.pollUrgentMs;
        setTimeout(() => {
          if (get().actif) get()._ouvrirStream();
        }, Math.max(1000, attente));
      });
  },
}));

export default useAlerteStore;
